import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { type Entity, identityEntityGetEntitiesOptions, identityEntityGetEntityOptions } from '@column/api';
import { Dropdown, DropdownOption } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  renderHighlight,
} from '~/lib/search/highlight';

// --- Label components ---

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'PERSON_DETAILS.EMAIL', label: 'Email' },
  { field: 'PERSON_DETAILS.PHONE_NUMBER', label: 'Phone Number' },
  { field: 'PERSON_DETAILS.SSN', label: 'SSN' },
  { field: 'BUSINESS_DETAILS.EIN', label: 'EIN' },
  { field: 'BUSINESS_DETAILS.WEBSITE', label: 'Website' },
  { field: 'DETAILS_ID', label: 'Details ID' },
  { field: 'ID', label: 'ID' },
];

const HighlightedLabel: FC<{ entity: Entity }> = ({ entity }) => {
  const highlightedName = getHighlightedField(entity.search_metadata, 'NAME');
  return <>{highlightedName ? renderHighlight(highlightedName) : entity.name || 'Unnamed'}</>;
};

const PlainLabel: FC<{ entity: Entity }> = ({ entity }) => <>{entity.name || 'Unnamed'}</>;

const EntityOptionLabel: FC<{
  entity: Entity;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ entity, highlightSearch, hasSearchQuery }) => {
  if (hasSearchQuery && highlightSearch && entity.search_metadata?.highlights) {
    return <HighlightedLabel entity={entity} />;
  }
  return <PlainLabel entity={entity} />;
};

const EntityOptionSmall: FC<{ entity: Entity }> = ({ entity }) => {
  const match = getHighlightedSuffix(entity.search_metadata, SUFFIX_CANDIDATES);
  if (!match) return null;
  return (
    <>
      {match.label}: {renderHighlight(match.fragment)}
    </>
  );
};

// --- Shared hook ---

interface UseEntityOptionsConfig {
  value?: string | null;
}

export const useEntityOptions = (config: UseEntityOptionsConfig = {}) => {
  const { value } = config;
  const [searchText, setSearchText] = useState<string | undefined>();

  const effectiveQuery = useMemo(() => {
    if (searchText) return { query: searchText };
    return undefined;
  }, [searchText]);

  const entitiesQuery = useQuery({
    ...identityEntityGetEntitiesOptions({ query: effectiveQuery }),
    placeholderData: keepPreviousData,
  });

  const resolvedId = value ?? undefined;

  type ListResponse = { entities?: Entity[]; has_more?: boolean } | undefined;
  const entities = (entitiesQuery.data as ListResponse)?.entities ?? [];

  const activeEntityQuery = useQuery({
    ...identityEntityGetEntityOptions({ path: { entity_id: resolvedId! } }),
    enabled: Boolean(resolvedId),
    placeholderData: () => entities.find((e) => e.id === resolvedId),
  });

  const activeEntity = activeEntityQuery.data as Entity | undefined;
  const hasSearchQuery = Boolean(searchText);

  const search = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (entity: Entity, highlightSearch: boolean): DropdownOption => ({
      label: <EntityOptionLabel entity={entity} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />,
      small:
        hasSearchQuery && highlightSearch && entity.search_metadata?.highlights ? (
          <EntityOptionSmall entity={entity} />
        ) : undefined,
      value: entity.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(() => entities.map((e) => toOption(e, true)), [entities, toOption]);

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      entities.find((e) => e.id === resolvedId) || (activeEntity?.id === resolvedId ? activeEntity : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeEntity, resolvedId, entities, toOption]);

  return {
    options,
    hiddenOptions,
    search,
    hasSearchQuery,
    entities,
    activeEntity,
  };
};

// --- Component ---

export interface EntityDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  searchLabel?: string;
}

export const EntityDropdown: FC<EntityDropdownProps> = ({
  value,
  onChange,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  isLoading,
  searchLabel,
}) => {
  const { options, hiddenOptions, search } = useEntityOptions({ value });

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No entities found"
      onSearchChange={(query: string) => search(query)}
      onChange={onChange}
      hasError={hasError}
      isDisabled={isDisabled}
      isLoading={isLoading}
      placeholder={placeholder}
      searchLabel={searchLabel}
      search
    />
  );
};
